/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { Fragment, FunctionComponent, useContext } from 'react';
import styled from 'styled-components';

import IntercomSvg from '@toucan/client/global/images/intercom.svg';

import {
    ApplicationContext,
    MediaQuery,
    useMediaQuery,
} from '@toucan/client/global/application';
import { IntercomContext } from '@toucan/client/global/intercom';

import { IntercomScript } from '../intercom-script';
import { styles } from './intercom.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface IntercomProps {
    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
}

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type StyledIntercomProps = IntercomProps & {
    stickyFooterHeight: number;
    shouldDisplayCustomLauncher: boolean;
};

const StyledIntercom = styled('button').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const pathnamesToExcludeOnSmallDisplays = [
    '/',
    '/basket',
    '/sell-form',
    '/quote-summary',
    '/quote-book-in',
    '/payment-link',
    '/checkout',
    '/smart-search',
];

/**
 * Checks pathname against a list of template paths.
 * If the pathname is in the list of exluded pathnames then we return false
 */
// eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
const isValidUrl = (path: string = '') => {
    return !pathnamesToExcludeOnSmallDisplays.some((excludedPath) => {
        const pathnameSlugParts = path.split('/');
        const excludedSlugParts = excludedPath.split('/');
        return pathnameSlugParts?.[1] === excludedSlugParts?.[1];
    });
};

/**
 * Intercom button, with lazy loading of the Intercom script
 */
export const Intercom: FunctionComponent<IntercomProps> = ({
    className,
    ...otherProps
}: IntercomProps) => {
    const { t } = useTranslation();
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);

    const { stickyFooterHeight } = useContext(ApplicationContext);
    const { isEnabled, isLauncherVisible, pendingCommand, openIntercom } =
        useContext(IntercomContext);

    const handleIntercomClick = () => {
        openIntercom();
    };

    const router = useRouter();

    const isValidUrlForSmallDisplays = isValidUrl(router.pathname);

    // if it's small display and URL isn't excluded OR any large display
    const shouldDisplayCustomLauncher =
        ((isValidUrlForSmallDisplays && isSmallTablet) || !isSmallTablet) &&
        isLauncherVisible;

    const preparedClassName = `${className} intercom-trigger`;

    return (
        <Fragment>
            <IntercomScript
                enabled={isEnabled}
                verticalPadding={stickyFooterHeight + 20}
                onLoadCommand={pendingCommand}
            />
            <StyledIntercom
                type="button"
                aria-label={t('Chat with us')}
                className={preparedClassName}
                onClick={handleIntercomClick}
                stickyFooterHeight={stickyFooterHeight}
                shouldDisplayCustomLauncher={shouldDisplayCustomLauncher}
                {...otherProps}
            >
                <IntercomSvg />
            </StyledIntercom>
        </Fragment>
    );
};
